import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Container,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { ZodError } from "zod";
import { motion } from "framer-motion";
import { useRouter } from "next/router";
import LottiePlayer from "../utilComp/LottiePlayer";
import animateData from "../../assets/lottieFiles/89292-thank-you-email.json";
import {
  ContactUsSchema,
  getInitialState,
  UserInfoType,
} from "./contactInfo";
import { useSubmitContactDetails } from "../../services/query/mutation";
import { getCurrentCountry, getTimeStamp } from "../../util/util";
import useAuthProvider from "../../store/AuthProvider";
import generateConfig from "../../util/config";

export interface ContactFromPros {
  formInitialData: UserInfoType;
}

export default function ContactUs({ formInitialData }: ContactFromPros) {
  const config = generateConfig();
  const contactEmail = config.contactEmail;
  const [userInfo, setUserInfo] = useState<UserInfoType>(formInitialData);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const router = useRouter();
  const auth = useAuthProvider();

  const { mutateAsync: submitContactForm, isLoading } = useSubmitContactDetails(
    {
      onSuccess: () => setIsSubmitted(true),
    }
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const data = ContactUsSchema.parse({
        name: userInfo.name.value,
        email: userInfo.email.value,
        mobNumber: userInfo.mobNumber.value,
      });
      const formData = {
        ...data,
        timeStamp: getTimeStamp(),
        countryCode: getCurrentCountry(),
      };
      submitContactForm(formData);
    } catch (e) {
      if (e instanceof ZodError) {
        const updatedUserInfo = { ...userInfo };
        e.errors.forEach((err) => {
          if (err.path?.[0]) {
            const keyValue = err.path?.[0] as keyof typeof updatedUserInfo;
            updatedUserInfo[keyValue] = {
              ...updatedUserInfo[keyValue],
              value: updatedUserInfo[keyValue].value,
              hasError: true,
            };
          }
        });
        setUserInfo(updatedUserInfo);
      }
    }
  };

  const backHomeHandler = () => {
    router.push("/");
  };

  useEffect(() => {
    if (auth.isAuthenticated) {
      setUserInfo((prev) => ({
        ...prev,
        name: {
          ...prev.name,
          value: auth.user?.name || "",
        },
        email: {
          ...prev.email,
          value: auth.user?.email || "",
        },
      }));
    } else {
      setUserInfo(() => getInitialState());
    }
  }, [auth.isAuthenticated, auth.user?.email, auth.user?.name]);

  return (
    <Box component="section" sx={{ py: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg">
        <Grid container spacing={8} alignItems="flex-start">
          <Grid item xs={12} md={5}>
            <Stack spacing={3}>
              <Typography variant="overline" sx={{ letterSpacing: 2, fontWeight: 600 }}>
                Let&apos;s collaborate
              </Typography>
              <Typography variant="h1" sx={{ fontSize: { xs: "2rem", md: "2.8rem" }, fontWeight: 700 }}>
                Share your idea and I&apos;ll shape it into an experience your users will love.
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Tell me about your project goals, ideal timeline, and the challenges you are hoping to solve. I reply to every message within two business days.
              </Typography>
              <Stack spacing={1.5}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Prefer email?
                </Typography>
                <Typography
                  component="a"
                  href={`mailto:${contactEmail}`}
                  sx={{ fontWeight: 500, textDecoration: "none" }}
                >
                  {contactEmail}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Currently based in Pune, collaborating with clients worldwide.
                </Typography>
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={12} md={7}>
            {isSubmitted ? (
              <Paper
                component={motion.div}
                elevation={0}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                sx={{
                  borderRadius: "24px",
                  p: { xs: 4, md: 6 },
                  textAlign: "center",
                  background: (theme) =>
                    `linear-gradient(135deg, ${theme.palette.primary.main}1a, ${theme.palette.secondary.main}33)`,
                  border: (theme) => `1px solid ${theme.palette.primary.light}`,
                }}
              >
                <Typography variant="h2" sx={{ fontWeight: 700, mb: 3 }}>
                  Thank you for reaching out!
                </Typography>
                <Typography variant="body1" sx={{ maxWidth: 520, mx: "auto", color: "text.secondary", mb: 4 }}>
                  I have received your message and will get back to you shortly. If you want to explore more in the meantime, head back to the home page.
                </Typography>
                <Box sx={{ maxWidth: 320, mx: "auto", mb: 4 }}>
                  <LottiePlayer autoplay loop src={animateData} style={{ width: "100%" }} />
                </Box>
                <LoadingButton
                  variant="contained"
                  onClick={backHomeHandler}
                  sx={{ px: 4, borderRadius: "999px" }}
                >
                  Back home
                </LoadingButton>
              </Paper>
            ) : (
              <Paper
                component={motion.form}
                onSubmit={handleSubmit}
                elevation={0}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                sx={{
                  borderRadius: "24px",
                  p: { xs: 4, md: 6 },
                  boxShadow: "0 30px 60px rgba(15, 23, 42, 0.12)",
                  border: "1px solid rgba(99, 102, 241, 0.15)",
                  background: (theme) => theme.palette.background.paper,
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                }}
              >
                <Stack spacing={3}>
                  <Typography variant="h3" sx={{ fontWeight: 600 }}>
                    Tell me about your project
                  </Typography>
                  <Alert severity="info" sx={{ borderRadius: "16px" }}>
                    Share a direct contact number so we can schedule a discovery call.
                  </Alert>
                  <TextField
                    value={userInfo.name.value}
                    type="text"
                    fullWidth
                    required
                    label="Full name"
                    placeholder="Jane Doe"
                    error={userInfo.name.hasError}
                    helperText={userInfo.name.hasError ? userInfo.name.errorMsg : " "}
                    onChange={(e) =>
                      setUserInfo((prev) => ({
                        ...prev,
                        name: {
                          ...prev.name,
                          value: e.target.value as string,
                          hasError: false,
                        },
                      }))
                    }
                  />
                  <TextField
                    value={userInfo.email.value}
                    type="email"
                    fullWidth
                    required
                    label="Email"
                    placeholder="you@example.com"
                    error={userInfo.email.hasError}
                    helperText={userInfo.email.hasError ? userInfo.email.errorMsg : " "}
                    onChange={(e) =>
                      setUserInfo((prev) => ({
                        ...prev,
                        email: {
                          ...prev.email,
                          value: e.target.value as string,
                          hasError: false,
                        },
                      }))
                    }
                  />
                  <TextField
                    value={userInfo.mobNumber.value}
                    type="tel"
                    fullWidth
                    required
                    label="Phone number"
                    placeholder="Include country code"
                    error={userInfo.mobNumber.hasError}
                    helperText={
                      userInfo.mobNumber.hasError
                        ? userInfo.mobNumber.errorMsg
                        : "Example: +1 987 654 3210"
                    }
                    onChange={(e) => {
                      const inputValue = e.target.value || "";
                      if (!/^\+?\d*$/.test(inputValue) || inputValue.length > 14) return;
                      setUserInfo((prev) => ({
                        ...prev,
                        mobNumber: {
                          ...prev.mobNumber,
                          value: inputValue,
                          hasError: false,
                        },
                      }));
                    }}
                  />
                  <LoadingButton
                    loading={isLoading}
                    variant="contained"
                    type="submit"
                    size="large"
                    sx={{
                      alignSelf: { xs: "stretch", sm: "flex-start" },
                      px: 4,
                      borderRadius: "999px",
                      fontWeight: 600,
                    }}
                  >
                    Send message
                  </LoadingButton>
                </Stack>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
